import { LeaderboardPlayerData } from "../../../models/leaderboard";
import { Group } from "../../../models/groupedTeams";
import { MatchSetupConfig } from "../../../models/match";
import { formatDateToDDMMMYYYY } from "../../../utils/dateUtils";
import { GroupLeaderboardMobileView } from "./GroupLeaderboardMobileView";

interface MatchScore {
  matchId: string;
  score?: number;
}

export interface EnhancedTeamData {
  teamId: string;
  teamName: string;
  teamLogo: string;
  groupName: string;
  totalScore: number;
  matchScores: MatchScore[];
}

interface TeamLeaderboardTableProps {
  data: LeaderboardPlayerData[];
  groups: Group[];
  matchSetups: MatchSetupConfig[];
}

const getTeamMatchWiseData = (
  groups: Group[],
  data: LeaderboardPlayerData[],
  matchSetups: MatchSetupConfig[]
): EnhancedTeamData[] => {
  const teamMap = new Map<string, EnhancedTeamData>();

  data.forEach((player) => {
    if (!player.playerForTeamId) return;

    if (!teamMap.has(player.playerForTeamId)) {
      const group = groups.find((g) =>
        g.teamIds?.includes(player.playerForTeamId!)
      );
      teamMap.set(player.playerForTeamId, {
        teamId: player.playerForTeamId,
        teamName: player.playerForTeamName || "Unknown Team",
        teamLogo: player.playerForTeamLogo || "",
        groupName: group?.name || "Ungrouped",
        totalScore: 0,
        matchScores: matchSetups.map((match) => ({
          matchId: match.matchId,
          score: 0,
        })),
      });
    }
  });

  teamMap.forEach((team) => {
    const playersForTeam = data.filter(
      (player) => player.playerForTeamId === team.teamId
    );
    matchSetups.forEach((match) => {
      if (match.gameFormat === "matchplay") {
        const matchScores = playersForTeam
          .filter((player) => player.matchId === match.matchId)
          .map((player) => player.totalPoints || 0);
        const matchTotalScore = matchScores.reduce(
          (sum, score) => sum + score,
          0
        );
        const matchScore = team.matchScores.find(
          (ms) => ms.matchId === match.matchId
        );
        if (matchScore) {
          matchScore.score = matchTotalScore;
        }
      } else if (match.playFormat === "doubles") {
        const selections = match.playerSelections?.find(
          (ps) => ps.teamId === team.teamId
        );
        if (!selections) {
          return;
        }
        const playerPairs = selections.players.reduce((acc, player) => {
          const holeDescription = player.holeDescription || "Unknown Hole";
          if (!acc[holeDescription]) {
            acc[holeDescription] = [];
          }
          acc[holeDescription].push(player);
          return acc;
        }, {} as Record<string, any[]>);

        const selectedPlayerIds: string[] = Object.values(playerPairs).map(
          (players) => players[0].playerId
        );

        // Calculate the total score for the top 3 pairs in the team
        const allScores = playersForTeam.filter(
          (player) =>
            selectedPlayerIds.includes(player.playerId) &&
            player.matchId === match.matchId
        );

        const topMatchScores = allScores
          .map((player) => player.totalStableford || 0)
          .sort((a, b) => b - a)
          .slice(0, 3);
        const matchTotalScore = topMatchScores.reduce(
          (sum, score) => sum + score,
          0
        );
        const matchScore = team.matchScores.find(
          (ms) => ms.matchId === match.matchId
        );
        if (matchScore) {
          matchScore.score = matchTotalScore;
        }
      } else {
        // Calculate the total score for the top 6 players in the team

        const topMatchScores = playersForTeam
          .filter((player) => player.matchId === match.matchId)
          .map((player) => player.totalStableford || 0)
          .sort((a, b) => b - a)
          .slice(0, 6);
        const matchTotalScore = topMatchScores.reduce(
          (sum, score) => sum + score,
          0
        );
        const matchScore = team.matchScores.find(
          (ms) => ms.matchId === match.matchId
        );
        if (matchScore) {
          matchScore.score = matchTotalScore;
        }
      }
    });
    team.totalScore = team.matchScores.reduce(
      (sum, ms) => sum + (ms.score ?? 0),
      0
    );
  });

  return Array.from(teamMap.values()).sort(
    (a, b) => b.totalScore - a.totalScore
  );
};

const GroupDataRow = ({
  groupName,
  teams,
  matchSetups,
}: {
  groupName: string;
  teams: EnhancedTeamData[];
  matchSetups: MatchSetupConfig[];
}) => {
  const sortedTeams = [...teams].sort((a, b) => b.totalScore - a.totalScore);

  return (
    <>
      <tr className="bg-gray-100 dark:bg-gray-800">
        <td colSpan={matchSetups.length + 3} className="px-4 py-2">
          <div className="flex items-center">
            <span className="font-bold text-gray-900 dark:text-gray-100">
              {groupName}
            </span>
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              ({teams.length} teams)
            </span>
          </div>
        </td>
      </tr>
      {sortedTeams.map((team, index) => (
        <TeamDataRow
          key={team.teamId}
          team={team}
          index={index}
          matchSetups={matchSetups}
          isTopTeam={index < 2}
        />
      ))}
    </>
  );
};

const TeamDataRow = ({
  team,
  index,
  matchSetups,
  isTopTeam,
}: {
  team: EnhancedTeamData;
  index: number;
  matchSetups: MatchSetupConfig[];
  isTopTeam: boolean;
}) => {
  return (
    <tr
      className={`hover:bg-gray-50 dark:hover:bg-gray-700
        ${isTopTeam ? "bg-green-50 dark:bg-green-900/20" : ""}`}
    >
      <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
        {index + 1}
        {isTopTeam && (
          <span className="ml-2 text-green-600 dark:text-green-400">
            {index === 0 ? "🏆" : "🥈"}
          </span>
        )}
      </td>
      <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
        <div className="flex items-center">
          {team.teamLogo && (
            <img
              src={`/api/static/images/${team.teamLogo}`}
              alt={team.teamName}
              className="w-8 h-8 rounded-full mr-2"
            />
          )}
          <span>{team.teamName}</span>
        </div>
      </td>
      {matchSetups.map((match: any) => {
        const teamScore = team.matchScores.find(
          (m) => m.matchId === match.matchId
        );
        return (
          <td
            key={match.id}
            className="px-4 py-2 whitespace-nowrap text-center text-gray-900 dark:text-gray-100"
          >
            {teamScore?.score || 0}
          </td>
        );
      })}
      <td className="px-4 py-2 whitespace-nowrap text-center font-bold text-gray-900 dark:text-gray-100">
        {team.totalScore}
      </td>
    </tr>
  );
};

const downloadTableAsCSV = (
  groupedTeams: Record<string, EnhancedTeamData[]>,
  matchSetups: MatchSetupConfig[]
) => {
  const rows: string[] = [];

  // Header row
  const headers = [
    "Rank",
    "Team",
    ...matchSetups.map(
      (match) =>
        `${match.courseName} - ${formatDateToDDMMMYYYY(match.date ?? "")}`
    ),
    "Total",
  ];
  rows.push(headers.join(","));

  // Data rows
  Object.entries(groupedTeams).forEach(([groupName, teams]) => {
    // Group header
    rows.push(`${groupName} (${teams.length} teams)`);

    // Team data
    teams
      .sort((a, b) => b.totalScore - a.totalScore)
      .forEach((team, index) => {
        const matchScores = matchSetups.map((match) => {
          const score = team.matchScores.find(
            (m) => m.matchId === match.matchId
          );
          return score?.score || 0;
        });

        const row = [index + 1, team.teamName, ...matchScores, team.totalScore];
        rows.push(row.join(","));
      });
  });

  // Create and trigger download
  const csvContent = "data:text/csv;charset=utf-8," + rows.join("\n");
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", "leaderboard.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const GroupLeaderboardTable = ({
  data,
  groups,
  matchSetups,
}: TeamLeaderboardTableProps) => {
  const teamData = getTeamMatchWiseData(
    groups || [],
    data || [],
    matchSetups || []
  );
  const groupedTeams = teamData.reduce((acc, team) => {
    if (!acc[team.groupName]) {
      acc[team.groupName] = [];
    }
    acc[team.groupName].push(team);
    return acc;
  }, {} as Record<string, EnhancedTeamData[]>);

  // Sort groups by name
  const sortedGroupEntries = Object.entries(groupedTeams).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  if (!data?.length || !matchSetups?.length) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No leaderboard data available
      </div>
    );
  }

  return (
    <>
      {/* Desktop View */}
      <div className="hidden md:block overflow-x-auto">
        <div className="relative">
          <div className="flex justify-end mb-4">
            <button
              onClick={() => downloadTableAsCSV(groupedTeams, matchSetups)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span>Download CSV</span>
            </button>
          </div>
          <div className="overflow-x-auto">
            {Object.keys(groupedTeams).length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No teams found in any groups
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-gray-900 dark:text-gray-100">
                      Rank
                    </th>
                    <th className="px-4 py-2 text-gray-900 dark:text-gray-100">
                      Team
                    </th>
                    {matchSetups.map((match: any) => (
                      <th
                        key={match.id}
                        className="px-4 py-2 text-center text-gray-900 dark:text-gray-100"
                      >
                        {match.courseName} -{formatDateToDDMMMYYYY(match.date)}
                      </th>
                    ))}
                    <th className="px-4 py-2 text-center text-gray-900 dark:text-gray-100">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedGroupEntries.map(([groupName, teams]) => (
                    <GroupDataRow
                      key={groupName}
                      groupName={groupName}
                      teams={teams}
                      matchSetups={matchSetups}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="md:hidden">
        <GroupLeaderboardMobileView
          groupedTeams={Object.fromEntries(sortedGroupEntries)}
          matchSetups={matchSetups}
        />
      </div>
    </>
  );
};
